import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  Home, Plus, Trash2, Edit2, FileText, Search, User, Phone,
  CheckCircle, Clock, X, Save, Printer, CreditCard, Banknote,
  AlertTriangle, PlusCircle, Receipt, Package, History
} from 'lucide-react';
import { printOrderTicketViaQz } from '@/utils/qzTrayService';

const API = process.env.REACT_APP_BACKEND_URL || window.location.origin;

// Configuration par défaut de l'imprimante
const DEFAULT_PRINTER_CONFIG = {
  useQzTray: true,
  printerName: '',
  ticketWidth: 58,
  shopName: 'GESTION GLOBALE',
  address: '',
  phone: ''
};

export default function ClientNotesPage() {
  const navigate = useNavigate();
  const [notes, setNotes] = useState([]);
  const [stats, setStats] = useState({ totalUnpaid: 0, totalPaid: 0, countUnpaid: 0, countPaid: 0 });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editingNote, setEditingNote] = useState(null);
  const [selectedNote, setSelectedNote] = useState(null);
  const [deletePassword, setDeletePassword] = useState('');
  const [filter, setFilter] = useState('unpaid'); // 'all', 'unpaid', 'paid'
  
  const [printerConfig] = useState(() => {
    const saved = localStorage.getItem('printerConfig');
    return saved ? JSON.parse(saved) : DEFAULT_PRINTER_CONFIG;
  });
  
  const [formData, setFormData] = useState({
    clientName: '',
    clientPhone: '',
    description: '',
    amount: '',
    date: new Date().toISOString().split('T')[0],
    notes: ''
  });

  const [addFormData, setAddFormData] = useState({
    description: '',
    amount: ''
  });

  const token = localStorage.getItem('token');

  useEffect(() => {
    fetchNotes();
    fetchStats();
  }, []);

  const fetchNotes = async () => {
    try {
      const response = await axios.get(`${API}/api/client-notes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotes(response.data.items || response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des notes');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/api/client-notes/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingNote) {
        await axios.put(`${API}/api/client-notes/${editingNote.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Note modifiée');
      } else {
        await axios.post(`${API}/api/client-notes`, {
          ...formData,
          amount: parseFloat(formData.amount)
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Note créée');
      }
      
      setShowModal(false);
      resetForm();
      fetchNotes();
      fetchStats();
    } catch (error) {
      toast.error('Erreur lors de l\'enregistrement');
    }
  };

  const handleAddToNote = async (e) => {
    e.preventDefault();
    
    try {
      await axios.post(`${API}/api/client-notes/${selectedNote.id}/add`, {
        clientName: selectedNote.clientName,
        description: addFormData.description,
        amount: parseFloat(addFormData.amount)
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Montant ajouté à la note');
      setShowAddModal(false);
      setAddFormData({ description: '', amount: '' });
      fetchNotes();
      fetchStats();
    } catch (error) {
      toast.error('Erreur lors de l\'ajout');
    }
  };

  const togglePayment = async (id) => {
    try {
      const response = await axios.patch(`${API}/api/client-notes/${id}/payment`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.isPaid ? 'Marqué comme réglé' : 'Marqué comme non réglé');
      fetchNotes();
      fetchStats();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/api/client-notes/${selectedNote.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Note supprimée');
      setShowDeleteModal(false);
      setDeletePassword('');
      setSelectedNote(null);
      fetchNotes();
      fetchStats();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const printNote = async (note) => {
    toast.loading('Impression en cours...', { id: 'print-note' });
    
    // Créer un objet formaté pour l'impression
    const printData = {
      productName: `CRÉDIT CLIENT`,
      quantity: 1,
      destination: note.clientName,
      clientName: note.clientName,
      clientPhone: note.clientPhone,
      notes: `${note.description}\n\nMontant: ${note.amount}€\nDate: ${new Date(note.date).toLocaleDateString('fr-FR')}\nStatut: ${note.isPaid ? 'RÉGLÉ' : 'NON RÉGLÉ'}`,
      priority: 'normal',
      status: note.isPaid ? 'paid' : 'pending',
      isPaid: note.isPaid,
      createdAt: note.createdAt
    };
    
    const result = await printOrderTicketViaQz(printData, printerConfig, printerConfig.printerName);
    
    if (result.success) {
      toast.success('Note imprimée', { id: 'print-note' });
    } else {
      toast.warning('Impression via navigateur...', { id: 'print-note' });
      printNoteFallback(note);
    }
  };

  const printNoteFallback = (note) => {
    const content = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Note Client</title>
        <style>
          body { font-family: 'Courier New', monospace; font-size: 12px; width: 72mm; margin: 0 auto; padding: 5mm; }
          .center { text-align: center; }
          .bold { font-weight: bold; }
          .big { font-size: 16px; font-weight: bold; }
          .separator { border-bottom: 1px dashed #000; margin: 5px 0; }
          .amount { font-size: 24px; font-weight: bold; text-align: center; }
          .status { border: 2px solid #000; padding: 5px; text-align: center; margin: 10px 0; }
        </style>
      </head>
      <body>
        <div class="center bold">GESTION GLOBALE</div>
        <div class="separator"></div>
        <div class="center big">NOTE CLIENT</div>
        <div class="center">${new Date(note.date).toLocaleDateString('fr-FR')}</div>
        <div class="separator"></div>
        <div class="bold">CLIENT:</div>
        <div>${note.clientName}</div>
        ${note.clientPhone ? `<div>Tel: ${note.clientPhone}</div>` : ''}
        <div class="separator"></div>
        <div class="bold">DESCRIPTION:</div>
        <div>${note.description}</div>
        <div class="separator"></div>
        <div class="amount">${note.amount.toFixed(2)} €</div>
        <div class="status">${note.isPaid ? '✓ RÉGLÉ' : '✗ NON RÉGLÉ'}</div>
        ${note.notes ? `<div class="separator"></div><div class="bold">NOTES:</div><div>${note.notes}</div>` : ''}
      </body>
      </html>
    `;
    
    const printWindow = window.open('', '_blank', 'width=400,height=600');
    printWindow.document.write(content);
    printWindow.document.close();
    printWindow.onload = () => printWindow.print();
  };

  const resetForm = () => {
    setFormData({
      clientName: '',
      clientPhone: '',
      description: '',
      amount: '',
      date: new Date().toISOString().split('T')[0],
      notes: ''
    });
    setEditingNote(null);
  };

  const openEditModal = (note) => {
    setEditingNote(note);
    setFormData({
      clientName: note.clientName,
      clientPhone: note.clientPhone || '',
      description: note.description,
      amount: note.amount.toString(),
      date: note.date,
      notes: note.notes || ''
    });
    setShowModal(true);
  };

  const filteredNotes = notes.filter(n => {
    const matchesSearch = n.clientName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         n.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filter === 'all' || 
                         (filter === 'unpaid' && !n.isPaid) || 
                         (filter === 'paid' && n.isPaid);
    return matchesSearch && matchesFilter;
  });

  return (
    <div className="min-h-screen bg-slate-900">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-gray-900 border-b border-gray-700 h-14 flex items-center justify-between px-6">
        <div className="flex items-center gap-4">
          <Button
            onClick={() => navigate('/')}
            variant="ghost"
            size="sm"
            className="text-white/80 hover:text-white hover:bg-white/10"
          >
            <Home className="w-4 h-4 mr-2" />
            Caisse
          </Button>
          <Button
            onClick={() => navigate('/invoices')}
            variant="ghost"
            size="sm"
            className="text-white/80 hover:text-white hover:bg-white/10"
          >
            <FileText className="w-4 h-4 mr-2" />
            Facturation
          </Button>
          <Button
            onClick={() => navigate('/deposit-receipts')}
            variant="ghost"
            size="sm"
            className="text-white/80 hover:text-white hover:bg-white/10"
          >
            <Receipt className="w-4 h-4 mr-2" />
            Bons d'entrée
          </Button>
          <Button
            onClick={() => navigate('/order-alerts')}
            variant="ghost"
            size="sm"
            className="text-white/80 hover:text-white hover:bg-white/10"
          >
            <Package className="w-4 h-4 mr-2" />
            Commandes
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="text-amber-400 bg-white/10"
          >
            <Banknote className="w-4 h-4 mr-2" />
            Notes Clients
          </Button>
        </div>
      </header>

      <main className="p-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div 
            onClick={() => setFilter('unpaid')}
            className={`p-4 rounded-xl cursor-pointer transition-all ${filter === 'unpaid' ? 'ring-2 ring-red-500' : ''} bg-gradient-to-br from-red-600 to-red-700 ${stats.countUnpaid > 0 ? 'animate-pulse' : ''}`}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white/70 text-sm">À récupérer</p>
                <p className="text-3xl font-bold text-white">{stats.totalUnpaid.toFixed(2)} €</p>
                <p className="text-white/50 text-xs">{stats.countUnpaid} note(s)</p>
              </div>
              <AlertTriangle className="w-10 h-10 text-white/50" />
            </div>
          </div>
          
          <div 
            onClick={() => setFilter('paid')}
            className={`p-4 rounded-xl cursor-pointer transition-all ${filter === 'paid' ? 'ring-2 ring-emerald-500' : ''} bg-gradient-to-br from-emerald-600 to-emerald-700`}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white/70 text-sm">Récupéré</p>
                <p className="text-3xl font-bold text-white">{stats.totalPaid.toFixed(2)} €</p>
                <p className="text-white/50 text-xs">{stats.countPaid} note(s)</p>
              </div>
              <CheckCircle className="w-10 h-10 text-white/50" />
            </div>
          </div>
          
          <div 
            onClick={() => setFilter('all')}
            className={`p-4 rounded-xl cursor-pointer transition-all ${filter === 'all' ? 'ring-2 ring-white' : ''} bg-gradient-to-br from-slate-700 to-slate-800`}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white/70 text-sm">Total</p>
                <p className="text-3xl font-bold text-white">{(stats.totalUnpaid + stats.totalPaid).toFixed(2)} €</p>
                <p className="text-white/50 text-xs">{stats.countUnpaid + stats.countPaid} note(s)</p>
              </div>
              <Banknote className="w-10 h-10 text-white/50" />
            </div>
          </div>
        </div>

        {/* Actions Bar */}
        <div className="flex items-center justify-between mb-6">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
            <Input
              placeholder="Rechercher par client ou description..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-white/10 border-white/20 text-white placeholder:text-white/40"
            />
          </div>
          <Button
            onClick={() => { resetForm(); setShowModal(true); }}
            className="bg-amber-500 hover:bg-amber-600"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nouvelle note
          </Button>
        </div>

        {/* Notes Table */}
        <div className="bg-white/5 rounded-xl border border-white/10 overflow-hidden">
          <table className="w-full">
            <thead className="bg-white/10">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium text-white/70">Date</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-white/70">Client</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-white/70">Description</th>
                <th className="px-4 py-3 text-right text-sm font-medium text-white/70">Montant</th>
                <th className="px-4 py-3 text-center text-sm font-medium text-white/70">Statut</th>
                <th className="px-4 py-3 text-right text-sm font-medium text-white/70">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/10">
              {loading ? (
                <tr>
                  <td colSpan="6" className="px-4 py-8 text-center text-white/50">
                    Chargement...
                  </td>
                </tr>
              ) : filteredNotes.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-4 py-8 text-center text-white/50">
                    Aucune note client
                  </td>
                </tr>
              ) : (
                filteredNotes.map((note) => (
                  <tr key={note.id} className="hover:bg-white/5">
                    <td className="px-4 py-3 text-white/70">
                      {new Date(note.date).toLocaleDateString('fr-FR')}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4 text-white/50" />
                        <div>
                          <div className="text-white font-medium">{note.clientName}</div>
                          {note.clientPhone && (
                            <div className="text-white/50 text-xs flex items-center gap-1">
                              <Phone className="w-3 h-3" />
                              {note.clientPhone}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-white/70 max-w-xs">
                      <div className="truncate" title={note.description}>
                        {note.description}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className={`text-lg font-bold ${note.isPaid ? 'text-emerald-400' : 'text-red-400'}`}>
                        {note.amount.toFixed(2)} €
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <Button
                        size="sm"
                        onClick={() => togglePayment(note.id)}
                        className={`h-8 px-3 ${
                          note.isPaid 
                            ? 'bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30' 
                            : 'bg-red-500/20 text-red-400 hover:bg-red-500/30 animate-pulse'
                        }`}
                      >
                        {note.isPaid ? (
                          <>
                            <CheckCircle className="w-4 h-4 mr-1" />
                            Réglé
                          </>
                        ) : (
                          <>
                            <Clock className="w-4 h-4 mr-1" />
                            Non réglé
                          </>
                        )}
                      </Button>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => { setSelectedNote(note); setShowAddModal(true); }}
                          className="text-amber-400 hover:text-amber-300 hover:bg-amber-500/10 h-7 w-7 p-0"
                          title="Ajouter à la note"
                        >
                          <PlusCircle className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => printNote(note)}
                          className="text-white/70 hover:text-white hover:bg-white/10 h-7 w-7 p-0"
                          title="Imprimer"
                        >
                          <Printer className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => openEditModal(note)}
                          className="text-white/70 hover:text-white hover:bg-white/10 h-7 w-7 p-0"
                          title="Modifier"
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => { setSelectedNote(note); setShowDeleteModal(true); }}
                          className="text-red-400 hover:text-red-300 hover:bg-red-500/10 h-7 w-7 p-0"
                          title="Supprimer"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </main>

      {/* Modal Création/Modification */}
      {showModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 rounded-xl border border-white/10 max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-white">
                  {editingNote ? 'Modifier la note' : 'Nouvelle note client'}
                </h2>
                <Button
                  variant="ghost"
                  onClick={() => { setShowModal(false); resetForm(); }}
                  className="text-white/70 hover:text-white"
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-white/70 mb-1">Nom / Prénom *</label>
                    <Input
                      value={formData.clientName}
                      onChange={(e) => setFormData({...formData, clientName: e.target.value})}
                      required
                      className="bg-white/10 border-white/20 text-white"
                      placeholder="Nom du client"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-white/70 mb-1">Téléphone</label>
                    <Input
                      value={formData.clientPhone}
                      onChange={(e) => setFormData({...formData, clientPhone: e.target.value})}
                      className="bg-white/10 border-white/20 text-white"
                      placeholder="06..."
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm text-white/70 mb-1">Description produit *</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    required
                    rows={3}
                    className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white placeholder:text-white/40 resize-none"
                    placeholder="Ex: Réparation écran iPhone 12..."
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-white/70 mb-1">Montant (€) *</label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.amount}
                      onChange={(e) => setFormData({...formData, amount: e.target.value})}
                      required
                      className="bg-white/10 border-white/20 text-white text-lg font-bold"
                      placeholder="0.00"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-white/70 mb-1">Date</label>
                    <Input
                      type="date"
                      value={formData.date}
                      onChange={(e) => setFormData({...formData, date: e.target.value})}
                      className="bg-white/10 border-white/20 text-white"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm text-white/70 mb-1">Notes supplémentaires</label>
                  <Input
                    value={formData.notes}
                    onChange={(e) => setFormData({...formData, notes: e.target.value})}
                    className="bg-white/10 border-white/20 text-white"
                    placeholder="Remarques..."
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => { setShowModal(false); resetForm(); }}
                    className="flex-1 border-white/20 text-white hover:bg-white/10"
                  >
                    Annuler
                  </Button>
                  <Button type="submit" className="flex-1 bg-amber-500 hover:bg-amber-600">
                    <Save className="w-4 h-4 mr-2" />
                    {editingNote ? 'Modifier' : 'Créer'}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Modal Ajouter à la note */}
      {showAddModal && selectedNote && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 rounded-xl border border-white/10 max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-white">
                  Ajouter à la note
                </h2>
                <Button
                  variant="ghost"
                  onClick={() => { setShowAddModal(false); setSelectedNote(null); }}
                  className="text-white/70 hover:text-white"
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>

              <div className="bg-white/5 rounded-lg p-3 mb-4">
                <p className="text-white/70 text-sm">Client</p>
                <p className="text-white font-medium">{selectedNote.clientName}</p>
                <p className="text-amber-400 font-bold text-lg mt-1">
                  Solde actuel: {selectedNote.amount.toFixed(2)} €
                </p>
              </div>

              <form onSubmit={handleAddToNote} className="space-y-4">
                <div>
                  <label className="block text-sm text-white/70 mb-1">Description *</label>
                  <Input
                    value={addFormData.description}
                    onChange={(e) => setAddFormData({...addFormData, description: e.target.value})}
                    required
                    className="bg-white/10 border-white/20 text-white"
                    placeholder="Ex: Coque de protection"
                  />
                </div>

                <div>
                  <label className="block text-sm text-white/70 mb-1">Montant à ajouter (€) *</label>
                  <Input
                    type="number"
                    step="0.01"
                    value={addFormData.amount}
                    onChange={(e) => setAddFormData({...addFormData, amount: e.target.value})}
                    required
                    className="bg-white/10 border-white/20 text-white text-lg font-bold"
                    placeholder="0.00"
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => { setShowAddModal(false); setSelectedNote(null); }}
                    className="flex-1 border-white/20 text-white hover:bg-white/10"
                  >
                    Annuler
                  </Button>
                  <Button type="submit" className="flex-1 bg-amber-500 hover:bg-amber-600">
                    <PlusCircle className="w-4 h-4 mr-2" />
                    Ajouter
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Modal Suppression avec authentification */}
      {showDeleteModal && selectedNote && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 rounded-xl border border-white/10 max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center justify-center mb-4">
                <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center">
                  <AlertTriangle className="w-8 h-8 text-red-400" />
                </div>
              </div>
              
              <h2 className="text-xl font-bold text-white text-center mb-2">
                Supprimer cette note ?
              </h2>
              
              <div className="bg-white/5 rounded-lg p-3 mb-4">
                <p className="text-white font-medium">{selectedNote.clientName}</p>
                <p className="text-white/70 text-sm">{selectedNote.description}</p>
                <p className="text-red-400 font-bold">{selectedNote.amount.toFixed(2)} €</p>
              </div>

              <p className="text-white/70 text-center text-sm mb-4">
                Cette action est irréversible.
              </p>

              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => { setShowDeleteModal(false); setSelectedNote(null); setDeletePassword(''); }}
                  className="flex-1 border-white/20 text-white hover:bg-white/10"
                >
                  Annuler
                </Button>
                <Button
                  onClick={handleDelete}
                  className="flex-1 bg-red-500 hover:bg-red-600"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Supprimer
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
